import {gmAPI} from '../../MapService';
import {simplify} from '../../../lib/geometry/simplify';
import {addDomMutation} from '@/services/GraphicService/Graphic.js';

export const MAX_SVG_VERTICES = 999;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

/**
 * @typedef {import('../../../lib/geometry/simplify').Point} PointXY
 * @typedef {import('./SVGOverlayView').SVGOverlayView} SVGOverlayView
 * @typedef {google.maps.MVCArray} MVCArray
 */

/**
 * The tolerance level for simplifying polys at each zoom level. Should result in very sparse polys when i (zoom)
 * is high. and very dense polys when i is low.
 *
 * The assumption is that a poly with a high number of vertices will ALSO be very large on the screen, so we can afford
 * to simplify it more. An extremely dense poly will still cause performance issues.
 *
 * TODO: better algorithms that relate the tolerance of the simplification to the average density of points per square
 * kilometer or perhaps to the standard deviation of the distances between points would be preferable.
 */
const toleranceAtZoom = Array(22).fill(0).map((_, i) => 2.5 / (2 ** i));

/**
 * Validates and converts a 2D array of LatLng or LatLngLiteral to a 2D array of LatLng.
 * @param {any[] | MVCArray} paths
 * @return {PointXY[][]}
 * @throws {TypeError} if `paths` is not a 2D array, or if any path is not an array, or if any latLng is not a LatLng or
 * LatLngLiteral
 */
function makeValidPaths(paths) {
  // validate that paths is an array of some type and has at least one path
  if (!paths || !(paths instanceof Array) || !paths.length) {
    return [];
  }
  // if paths is a 1D array, wrap it in another array to normalize
  if (!(paths[0] instanceof Array)) {
    paths = [paths];
  }
  // convert any LatLngLiteral paths to LatLng paths
  return paths.map((path) => {
    // validate that each path is an array
    if (!(path instanceof Array) || !path.length) {
      throw new TypeError('empty path');
    }
    return path.map((latLng) => {
      // validate that each latLng is a LatLng or LatLngLiteral
      if (typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
        return [latLng.lng(), latLng.lat()];
      } else if (latLng.lat != null && latLng.lng != null) {
        return [latLng.lng, latLng.lat];
      } else if (latLng.length === 2) {
        return latLng;
      } else {
        throw new TypeError('path contains a non-LatLng');
      }
    });
  });
}

/**
 * Validates the path option and updates the SVG element with the new paths and updates the bounds.
 * @param {SVGOverlayView} overlayView
 * @param {google.maps.PolygonOptions & google.maps.PolylineOptions} options
 * @param {Record<string, string>} attrMap maps the Polygon/Polyline options to the SVG attributes
 * @param {google.maps.PolygonOptions | google.maps.PolylineOptions} defaultOptions default options for the SVG element
 */
function setSVGOverlayViewOptions(overlayView, options, attrMap, defaultOptions) {
  if (!options.paths && options.path) {
    options.paths = [options.path];
  }
  try {
    overlayView.paths = makeValidPaths(options.paths);
  } catch (e) {
    throw new Error(`Error setting options on SVGOverlayView: ${e.message}`);
  }

  overlayView.simplifiedPaths = overlayView.paths.map((path) => {
    return toleranceAtZoom.map((tolerance) => path.length <= MAX_SVG_VERTICES ? path : simplify(path, tolerance));
  });

  overlayView.bounds = getBoundsFromPath(overlayView.paths.flat());

  // remove any existing paths from the SVGs
  [overlayView.displaySvg, overlayView.clickSvg].forEach((svg) => {
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
  });

  overlayView.options = {...defaultOptions, ...options};
  // valid paths are stored in overlayView.paths, no need to store un-validated paths
  delete overlayView.options.paths;
  delete overlayView.options.path;

  overlayView.pathElements = [];
  overlayView.displayUseElements = [];
  overlayView.clickUseElements = [];
  if (overlayView.svgType === 'polygon') {
    const displayDefElement = document.createElementNS(SVG_NAMESPACE, 'defs');
    const useElements = overlayView.paths.map((path, idx) => {
      const isClosed = isClosedPath(path);
      const rand = Math.random().toString(36).substring(7);
      const pathId = `${overlayView.id}${rand}`;
      const displayClipPathElement = createClipPathElement(pathId);
      const displayPathElement = createPathElement(overlayView.options, attrMap, pathId, isClosed);
      const displayUseElement = createUseElement(overlayView.options, attrMap, pathId, isClosed);
      const clickUseElement = createUseElement(overlayView.options, attrMap, pathId, isClosed);
      // The click SVG paths must be completely transparent regardless of chosen options.
      clickUseElement.style.strokeOpacity = '0';
      clickUseElement.style.fillOpacity = '0';
      // These are new elements, make sure they have the same listeners as the old elements
      overlayView.listeners.forEach((listener) => {
        clickUseElement.addEventListener(listener.eventName, listener.handler);
      });
      displayDefElement.appendChild(displayPathElement);
      displayDefElement.appendChild(displayClipPathElement);
      overlayView.pathElements.push(displayPathElement);
      overlayView.displayUseElements.push(displayUseElement);
      overlayView.clickUseElements.push(clickUseElement);
      return [displayUseElement, clickUseElement];
    });
    addDomMutation(() => {
      useElements.forEach(([displayUseElement, clickUseElement]) => {
        overlayView.displaySvg.appendChild(displayUseElement);
        overlayView.clickSvg.appendChild(clickUseElement);
      });
      overlayView.displaySvg.appendChild(displayDefElement);
    });
  } else {
    const displayDefElement = document.createElementNS(SVG_NAMESPACE, 'defs');
    const useElements = overlayView.paths.map((path, idx) => {
      const isClosed = isClosedPath(path);
      const rand = Math.random().toString(36).substring(7);
      const pathId = `${overlayView.id}${rand}`;
      const displayPe = createNonPolygonPathElement(overlayView.options, attrMap, pathId, isClosed);
      const displayUseElement = createUseElement(overlayView.options, attrMap, pathId, isClosed);
      const clickUseElement = createUseElement(overlayView.options, attrMap, pathId, isClosed);
      // The click SVG paths must be completely transparent regardless of chosen options.
      clickUseElement.style.strokeOpacity = '0';
      clickUseElement.style.fillOpacity = '0';
      clickUseElement.style.stroke = 'rgba(0,0,0,0)';
      // These are new elements, make sure they have the same listeners as the old elements
      overlayView.listeners.forEach((listener) => {
        clickUseElement.addEventListener(listener.eventName, listener.handler);
      });
      displayDefElement.appendChild(displayPe);
      overlayView.pathElements.push(displayPe);
      overlayView.displayUseElements.push(displayUseElement);
      overlayView.clickUseElements.push(clickUseElement);
      return [displayUseElement, clickUseElement];
    });
    addDomMutation(() => {
      useElements.forEach(([displayUseElement, clickUseElement]) => {
        overlayView.displaySvg.appendChild(displayUseElement);
        overlayView.clickSvg.appendChild(clickUseElement);
      });
      overlayView.displaySvg.appendChild(displayDefElement);
    });
  }
}

/**
 * @return {SVGSVGElement}
 */
function createSVGElement() {
  const elt = document.createElementNS(SVG_NAMESPACE, 'svg');
  elt.setAttribute('xmlns:xlink', XLINK_NAMESPACE);
  elt.setAttribute('xmlns', SVG_NAMESPACE);
  elt.style.position = 'absolute';
  elt.style.strokeLinecap = 'round';
  elt.style.strokeLinejoin = 'round';
  return elt;
}

/**
 * @param {google.maps.PolygonOptions | google.maps.PolylineOptions} options Polygon/Polyline options
 * @param {Record<string, string>} attrMap maps the Polygon/Polyline options to the SVG attributes
 * @param {string} id
 * @param {boolean} [isClosed=false] indicates whether this is a closed polygon path or an open polyline path
 * @return {SVGPathElement}
 */
function createNonPolygonPathElement(options, attrMap, id, isClosed = false) {
  const elt = document.createElementNS(SVG_NAMESPACE, 'path');
  Object.entries(options).forEach(([key, value]) => {
    // SVG and Polygon/Polyline have a 1:1 relationship with the attributes we are interested in, but they have
    // different names. Assume that the incoming options are in the Polygon/Polyline format and convert them to SVG
    if (key in attrMap) {
      if (key === 'strokeColor') return;
      if (key === 'fillColor' && !isClosed) {
        value = 'none';
      }
      if (key.toLowerCase().includes('weight')) {
        value = `${value}px`;
      }
      elt.style[attrMap[key]] = value;
    }
  });
  // Only the visible portion of the SVG (the path) should be clickable
  elt.style.pointerEvents = 'painted';
  elt.classList.add('overlay');
  elt.id = `polygon${id}`;
  return elt;
}

/**
 * @param {google.maps.PolygonOptions | google.maps.PolylineOptions} options Polygon/Polyline options
 * @param {Record<string, string>} attrMap maps the Polygon/Polyline options to the SVG attributes
 * @param {string} id element uniquie id
 * @param {boolean} [isClosed=false] indicates whether this is a closed polygon path or an open polyline path
 * @return {SVGPathElement}
 */
function createPathElement(options, attrMap, id, isClosed = false) {
  const elt = document.createElementNS(SVG_NAMESPACE, 'path');
  // Only the visible portion of the SVG (the path) should be clickable
  elt.id = `polygon${id}`;
  elt.setAttribute('clip-path', `url(#insidePolygonOnly${id})`);
  return elt;
}

/**
 * @param {string} id path unique id
 * @return {SVGClipPathElement}
 */
function createClipPathElement(id) {
  const clipPathElement = document.createElementNS(SVG_NAMESPACE, 'clipPath');
  clipPathElement.id = `insidePolygonOnly${id}`;
  const useElement = document.createElementNS(SVG_NAMESPACE, 'use');
  useElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `#polygon${id}`);
  clipPathElement.appendChild(useElement);
  return clipPathElement;
}

/**
 * @param {google.maps.PolygonOptions | google.maps.PolylineOptions} options Polygon/Polyline options
 * @param {Record<string, string>} attrMap maps the Polygon/Polyline options to the SVG attributes
 * @param {string} id unique path id
 * @param {boolean} [isClosed=false] indicates whether this is a closed polygon path or an open polyline path
 * @return {SVGUseElement}
 */
function createUseElement(options, attrMap, id, isClosed = false) {
  const useElement = document.createElementNS(SVG_NAMESPACE, 'use');
  useElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `#polygon${id}`);
  useElement.style.pointerEvents = 'painted';
  useElement.classList.add('overlay');
  Object.entries(options).forEach(([key, value]) => {
    // SVG and Polygon/Polyline have a 1:1 relationship with the attributes we are interested in, but they have
    // different names. Assume that the incoming options are in the Polygon/Polyline format and convert them to SVG
    if (!(key in attrMap)) return;
    if (key === 'fillColor' && !isClosed) {
      value = 'none';
    }
    if (key === 'strokeWeight') {
      value *=2;
    }
    if (key === 'fillColor' || key === 'strokeColor') {
      useElement.style[attrMap[key]] = value;
    } else {
      useElement.setAttribute(attrMap[key], value);
    }
  });
  return useElement;
}

/**
 * @param {PointXY} point
 * @return {string}
 */
function pointToString(point) {
  return `${point[0]},${point[1]}`;
}

/**
 * @param {PointXY[]} points
 * @return {string}
 */
function makeDAttribute(points) {
  if (!points.length) return '';
  return [`M ${pointToString(points[0])}`]
    .concat(points.slice(1).map((pt) => `L ${pointToString(pt)}`))
    .join(' ');
}

/**
 * @param {PointXY[]} path
 * @return {google.maps.LatLngBounds}
 */
function getBoundsFromPath(path) {
  const bounds = new gmAPI.LatLngBounds();
  path.forEach((point) => {
    bounds.extend({lat: point[1], lng: point[0]});
  });
  return bounds;
}

/**
 * @param {PointXY[]} path
 * @return {boolean}
 */
function isClosedPath(path) {
  const first = path[0];
  const last = path[path.length - 1];
  return first[0] === last[0] && first[1] === last[1];
}

export {
  createSVGElement,
  makeDAttribute,
  setSVGOverlayViewOptions,
  isClosedPath,
};
